/* Click Fencing - glass calculator fixes.
 *
 * 1. RAKE PANEL FLOATED ABOVE ITS SPIGOTS. ullc-calc.js draws each glass rake panel as its
 *    own inline <svg width=108 height=135> at top:0 inside its stage. cf-preview-legible.js
 *    ("#section-preview svg") and cf-preview-scroll.js (".panels-container svg") were written
 *    for the aluminium fence drawing but also match this PER-PANEL artwork and resize it, and
 *    a shrunk svg pinned at top:0 leaves its gap at the BOTTOM. See fix 6 - the seating rule
 *    changed on 2026-09-02 from "sit above my own spigots" to "sit on the same ground line as
 *    the panels either side of me", which is what Pete actually asked for.
 *
 * 2. GATE HINGE POPUP REMOVED. Pete: "how you have it is good. no need to put a warning at
 *    all." The auto-downgrade to wall-mounted hinges is unchanged, it just no longer
 *    interrupts.
 *
 * 3. CORNER LATCHES WERE MISLABELLED. The line was built with a two-way test
 *    (/wall/i.test(latchType) ? "Wall to Glass" : "Glass to Glass"), so both corner types
 *    printed as "Glass to Glass" while charging correctly ($88.83 / $90.92). Rewritten from
 *    section.latchType; lines pair with gated sections in document order.
 *
 * 4. A DRAWN GAP COULD EXCEED THE 100mm POOL LIMIT. Set a 4500mm raked section with a gate,
 *    choose the widest gap (80mm), then change the length to 3000mm: the preview drew
 *    80, 7, 10, 103. The SOLVER was right - gapSpacings held [92, 10, 7, 91] - the preview
 *    just spread the same 200mm total lopsidedly, and the gap elements are fixed 13px spacers
 *    so the label IS the drawing. The variable gaps are re-spread evenly over the same total,
 *    leaving the two gate hardware gaps (7mm latch, 10mm hinge) where the preview put them.
 *    Only acts when genuinely wrong: gaps within 1mm of each other and <= 100mm are left
 *    alone, so ordinary fences are untouched. It does NOT force a gap down to the customer's
 *    chosen maximum - Pete: "leave 13mm to 19mm etc".
 *
 * ===========================================================================================
 * 5. THE RETAINED-HEIGHT WARNING NO LONGER BLOCKS THE SALE. Rewritten 2026-09-02.
 *
 *    Pete: "this rake panel warning is not letting orders go through. move this warning to the
 *    specific section the rake is on and make it a pop up warning that needs to be resolved
 *    before the customer clicks through to the next section."
 *
 *    WHAT IT USED TO DO, and why it stopped sales. When a raked section had no Retained Height
 *    chosen it (a) deleted the rake panel line out of the quote, (b) printed a red banner at
 *    the top of the WHOLE Quote Breakdown, and (c) set add-to-cart .disabled = true. The
 *    customer reached the last screen, found a warning about a dropdown three steps behind
 *    them, and a dead Add to Cart. That is the defect.
 *
 *    WHAT IT DOES NOW. The check moved to where the mistake is actually made. Measured on the
 *    live calculator: gc.validateSection(1) returns TRUE for a raked section with
 *    section-1-retained-height set to "" - the calculator's own per-section validation does
 *    not look at it at all. So validateSection is wrapped: a raked section with no retained
 *    height fails, which stops handleSectionNextClick advancing, and the calculator's own
 *    popup (showValidationPopup) explains it. The dropdown is outlined and scrolled to.
 *
 *    Because the customer can no longer LEAVE the section without choosing, the three blunt
 *    instruments are gone: the quote line is no longer deleted, the breakdown banner is no
 *    longer drawn, and add-to-cart is never disabled by this file. Any block a previous
 *    version of this file left on the button is released on load, so a customer sitting on a
 *    stale page is not stuck.
 *
 * 6. THE RAKE PANEL DID NOT LINE UP ON THE BOTTOM, AND ON /pages/quote-review IT WAS NEVER
 *    EVEN SEATED. Pete: "these rake panel drawings are a big issue. make them line up on the
 *    bottom and show up properly on the graphics in these places."
 *
 *    MEASURED, 8790mm raked section, gate 800 LHS, on the live preview. Only the RAKE panel
 *    has artwork at all - every other panel is a styled <div>:
 *
 *        panel                 glass div      bottom
 *        1200mm Raked Panel    t641 b841 h200   <- its own inline <svg>
 *        1700 Hinge            t679 b841 h163
 *        800 Gate              t679 b841 h163
 *        1700                  t680 b843 h163
 *        1650                  t680 b843 h163
 *        1650                  t680 b843 h163
 *
 *    Two different rendering systems on one row, so alignment was only ever coincidence. The
 *    old rule seated the svg 2px above ITS OWN spigots, which is not the same ground line the
 *    plain panels stand on - hence the 841 vs 843 split above, and worse elsewhere.
 *
 *    The rule is now: every panel in a row stands on the SAME ground line, taken as the most
 *    common bottom edge of the plain (non-rake) glass divs beside it. A rake is legitimately
 *    TALLER - it follows the slope - so only the bottom is matched; the top is left alone.
 *
 *    AND IT NOW RUNS ON /pages/quote-review. The old file installed by wrapping the
 *    GlassFencingCalculator prototype, which only exists on the calculator page, so on the
 *    quote page install() returned false and NOTHING ran - which is why the Final Preview /
 *    Section Details drawing was the worse of the two. Seating and gap-fixing are now
 *    installed on any page that has .panel-preview elements, independently of the prototype.
 *
 * Shape follows cf-calc-fixes.js: IIFE, prototype flag, polled install with a try limit,
 * every wrapper in try/catch so a display fix can never break the calculator.
 */
(function () {
  'use strict';

  var TRIES = 0, MAX_TRIES = 200, GAP_LIMIT = 100;
  var NOTICE_ID = 'cf-rake-height-notice', BLOCK_ATTR = 'data-cf-rake-blocked';
  var FIELD_FLAG = 'data-cf-rake-flagged';

  var HINGE_MSG =
    /A\s*\d+\s*mm gate needs a 600mm hinge panel to hang off glass[\s\S]*?(?:and a wall latch\.|latches to the glass panel beside it\.)/gi;
  var LATCH_NAMES = /(Glass to Glass|Wall to Glass|Corner Outswing|Corner Inswing)$/;

  function instance() { return window.glassCalculator || window.glassFencingCalculator || null; }

  /* ---------------------------------------------------------------- 6. seating */

  function isRakePanel(p) {
    var l = p.querySelector('.panel-width');
    return !!(l && /rake/i.test(l.textContent || ''));
  }

  /* The glass itself is the first child div of .panel-preview - the label is .panel-width and
     there is a zero-height spacer between them. Measured on the live page. */
  function glassBox(p) {
    var k = p.children;
    for (var i = 0; i < k.length; i++) {
      if (k[i].classList && k[i].classList.contains('panel-width')) continue;
      var r = k[i].getBoundingClientRect();
      if (r.height > 4) return r;
    }
    return null;
  }

  /* The ground line: the most common bottom edge among the PLAIN panels in this row. Mode
     rather than max, so one already-misplaced panel cannot drag the whole row. */
  function groundLine(row) {
    var tally = {}, best = null, bestN = 0;
    for (var i = 0; i < row.length; i++) {
      if (isRakePanel(row[i])) continue;
      var b = glassBox(row[i]);
      if (!b) continue;
      var key = Math.round(b.bottom);
      tally[key] = (tally[key] || 0) + 1;
      if (tally[key] > bestN) { bestN = tally[key]; best = key; }
    }
    return best;
  }

  /* A ROW IS A .panels-container, NOT A PARENT NODE. Measured 2026-09-02: every .panel-preview
     sits in its OWN wrapper div - six panels, six distinct parents - so grouping by parentNode
     put each panel in a row of one, the rake row had no plain panel to measure against,
     groundLine() returned null and the seating skipped every time. The real row is the nearest
     ancestor holding more than one panel, which is .panels-container on the calculator and the
     per-section container on /pages/quote-review. Found by walking up rather than hard-coding a
     class, so it keeps working if either page renames its wrapper. */
  function rowAncestor(p) {
    var el = p.parentNode, hops = 0;
    while (el && hops < 8) {
      if (el.querySelectorAll && el.querySelectorAll('.panel-preview').length > 1) return el;
      el = el.parentNode; hops++;
    }
    return null;
  }

  function rowsOfPanels() {
    var all = [].slice.call(document.querySelectorAll('.panel-preview'));
    var rows = [];
    for (var i = 0; i < all.length; i++) {
      var key = rowAncestor(all[i]);
      if (!key) continue;                      /* a lone panel - nothing to line it up with */
      var idx = -1;
      for (var j = 0; j < rows.length; j++) if (rows[j].parent === key) { idx = j; break; }
      if (idx < 0) { rows.push({ parent: key, items: [] }); idx = rows.length - 1; }
      rows[idx].items.push(all[i]);
    }
    return splitByLine(rows);
  }

  /* 2026-10-09: on a phone a long glass fence wraps into ROWS inside one container (cf-preview-phone.js),
     so one container can hold two or three visual lines, each with its own ground. Seating a rake to the
     container's most common ground pushed it down onto the next line (Pete: Review page test, rake + two
     gates). Split each group into lines: slots on one line share a bottom edge (the row is
     align-items:flex-end), lines are far apart. On one line (desktop, short fences) nothing changes. */
  function splitByLine(groups) {
    var out = [];
    groups.forEach(function (g) {
      var lines = [];
      g.items.forEach(function (p) {
        var slot = p;
        while (slot.parentNode && slot.parentNode !== g.parent) slot = slot.parentNode;
        var b = slot.getBoundingClientRect ? slot.getBoundingClientRect().bottom : 0;
        var hit = null;
        for (var k = 0; k < lines.length; k++) if (Math.abs(lines[k].b - b) < 20) { hit = lines[k]; break; }
        if (!hit) { hit = { b: b, items: [] }; lines.push(hit); }
        hit.items.push(p);
      });
      if (lines.length < 2) { out.push(g); return; }
      lines.forEach(function (l) { out.push({ parent: g.parent, items: l.items }); });
    });
    return out;
  }


  function seatOnce() {
    var worst = 0, rows = rowsOfPanels();
    for (var r = 0; r < rows.length; r++) {
      var row = rows[r].items;
      var ground = groundLine(row);
      if (ground === null) continue;               /* a row of only rakes - nothing to match */
      for (var i = 0; i < row.length; i++) {
        var p = row[i];
        if (!isRakePanel(p)) continue;
        var art = p.querySelector('svg') || p.querySelector('img');
        if (!art) continue;

        /* cf-preview-legible / cf-preview-scroll shrink this per-panel artwork; put its own
           intrinsic size back before measuring, or the seating chases a moving target. */
        var w = art.getAttribute && art.getAttribute('width');
        var h = art.getAttribute && art.getAttribute('height');
        if (w && h) {
          art.style.setProperty('width', w + 'px', 'important');
          art.style.setProperty('height', h + 'px', 'important');
        }
        art.style.setProperty('top', 'auto', 'important');

        var prev = parseFloat(art.style.bottom) || 0;
        var cur = art.getBoundingClientRect().bottom;
        var delta = ground - cur;
        if (Math.abs(delta) > Math.abs(worst)) worst = delta;
        if (Math.abs(delta) > 0.5) art.style.setProperty('bottom', (prev - delta) + 'px', 'important');
      }
    }
    return Math.abs(worst);
  }

  /* SEATING HANDED OVER TO cf-rake-align.js, 2026-09-02.

     Pete: "it seems there is a flicker on the rake panel now. when i first put it in it is to
     low then moves to the right position. then i add a gate and it lowers then goes into the
     right position?"

     Two files were seating the same artwork to two DIFFERENT lines. groundLine() above uses
     glassBox(), which returns the first child of .panel-preview with real height - and that is
     the WRAPPER, whose bottom is the spigot line. The glass itself is one level deeper and
     13px higher. Measured on a live plain panel:

         wrapper   top 626  bottom 789      <- what this file aims at
         GLASS     top 626  bottom 776      <- what the customer sees as the base
         spigots   top 771  bottom 791

     So on every redraw this file pushed the rake down to 789 and cf-rake-align.js pulled it
     back up to 776 - "it lowers then goes into the right position", exactly as described. It
     settles at rest only because this file has no permanent timer; the fight is visible for
     the length of a redraw, every redraw.

     Pete's rule is the GLASS line, not the spigots ("not the bottom of the spigots"), and
     cf-rake-align.js also has to size the panel so the change of angle meets the neighbouring
     panel tops - a job this file never did. One owner, so this one stands down whenever that
     file is present. Everything else here is untouched, and if cf-rake-align is ever removed
     the old seating comes straight back rather than leaving the rake unseated. */
  function seatRakePanels() {
    if (window.__cfRakeAlign) return;
    try { for (var pass = 0; pass < 3; pass++) { if (seatOnce() <= 0.5) break; } } catch (e) {}
  }

  /* ---------------------------------------------------------------- 3. latch labels */

  function fixLatchLabels(inst) {
    try {
      var secs = inst && inst.state && inst.state.sections;
      if (!secs) return;
      var order = Object.keys(secs).map(Number).sort(function (a, b) { return a - b; })
        .filter(function (n) { return secs[n] && secs[n].hasGate; });
      if (!order.length) return;
      /* 2026-10-03: a section can carry TWO gates (cf-two-gates.js). Its latch lines come in gate
         order and each gate has its own latch, which that file records on the solved layout as
         cfLatchOrder - so the list below is one entry per LATCH LINE, not per section. */
      var wantList = [];
      order.forEach(function (n) {
        var c = inst.state.optimizedConfigs && inst.state.optimizedConfigs[n];
        if (c && c.cfGates && Array.isArray(c.cfLatchOrder) && c.cfLatchOrder.length) wantList = wantList.concat(c.cfLatchOrder);
        else wantList.push(secs[n].latchType);
      });
      var spans = document.querySelectorAll('span.item-name'), idx = 0;
      for (var i = 0; i < spans.length; i++) {
        var el = spans[i], txt = el.textContent.replace(/\s+/g, ' ').trim();
        if (!/^Gate Latch\b/.test(txt)) continue;
        var tail = txt.match(LATCH_NAMES);
        if (!tail) { idx++; continue; }
        var head = txt.slice(0, txt.length - tail[1].length).replace(/\s+$/, '');
        var want = wantList[Math.min(idx, wantList.length - 1)];
        if (want && want !== tail[1]) el.textContent = head + ' ' + want;
        idx++;
      }
    } catch (e) {}
  }

  /* ---------------------------------------------------------------- 4. gap spread */

  function fixGaps() {
    try {
      var pEls = document.querySelectorAll('.panel-width'), gEls = document.querySelectorAll('.gap-width');
      if (!gEls.length || gEls.length !== pEls.length + 1) return;
      var labs = [], i;
      for (i = 0; i < pEls.length; i++) labs.push(pEls[i].textContent.replace(/^\d+mm\s*/, '').toLowerCase());
      // Every gate's hinge and latch gaps are hardware - a section can hold two gates
      // (cf-two-gates.js, 2026-10-03), and the second gate's 7/10mm gaps are not spreadable.
      var hw = {};
      for (i = 0; i < labs.length; i++) if (/gate/.test(labs[i])) { hw[i] = 1; hw[i + 1] = 1; }
      var vals = [];
      for (i = 0; i < gEls.length; i++) vals.push(parseInt(gEls[i].textContent, 10) || 0);
      var varIdx = [];
      for (i = 0; i < vals.length; i++) if (!hw[i]) varIdx.push(i);
      if (varIdx.length < 2) return;
      var vv = [];
      for (i = 0; i < varIdx.length; i++) vv.push(vals[varIdx[i]]);
      var mx = Math.max.apply(null, vv), mn = Math.min.apply(null, vv);
      if (mx <= GAP_LIMIT && (mx - mn) <= 1) return;
      var total = 0;
      for (i = 0; i < vv.length; i++) total += vv[i];
      var n = varIdx.length, base = Math.floor(total / n), rem = total - base * n;
      for (i = 0; i < n; i++) {
        var want = base + (i < rem ? 1 : 0);
        if (vals[varIdx[i]] !== want) gEls[varIdx[i]].textContent = String(want);
      }
    } catch (e) {}
  }

  /* ------------------------------------------- 5. retained height, at the section */

  function retainedMissing(inst, n) {
    try {
      var s = inst && inst.state && inst.state.sections && inst.state.sections[n];
      if (!s || !s.hasRaked) return false;
      var sel = document.getElementById('section-' + n + '-retained-height');
      var chosen = sel ? String(sel.value || '').trim() : String(s.retainedHeight || '').trim();
      return !chosen;
    } catch (e) { return false; }
  }

  function flagField(n) {
    try {
      var sel = document.getElementById('section-' + n + '-retained-height');
      if (!sel) return;
      sel.setAttribute(FIELD_FLAG, '1');
      sel.style.setProperty('border', '2px solid #c0392b', 'important');
      sel.style.setProperty('background', '#fdf3f2', 'important');
      try { sel.scrollIntoView({ behavior: 'smooth', block: 'center' }); } catch (e) {}
      try { sel.focus(); } catch (e) {}
      if (!sel.__cfRakeClear) {
        sel.__cfRakeClear = true;
        sel.addEventListener('change', function () { unflagField(n); });
      }
    } catch (e) {}
  }

  function unflagField(n) {
    try {
      var sel = document.getElementById('section-' + n + '-retained-height');
      if (!sel || !sel.getAttribute(FIELD_FLAG)) return;
      sel.removeAttribute(FIELD_FLAG);
      sel.style.removeProperty('border');
      sel.style.removeProperty('background');
    } catch (e) {}
  }

  var RAKE_MSG = 'This section has a raked panel, so it needs a Retained Height before you ' +
                 'can continue. The raked panel is made to suit your slope and cannot be ' +
                 'priced until you choose it. Pick a Retained Height for section ';

  /* Anything an older version of this file disabled or drew is undone, so a customer sitting
     on a stale page is never left with a dead Add to Cart. */
  function clearLegacyBlock() {
    try {
      var old = document.getElementById(NOTICE_ID);
      if (old && old.parentNode) old.parentNode.removeChild(old);
      var btn = document.getElementById('add-to-cart');
      if (btn && btn.getAttribute(BLOCK_ATTR)) {
        btn.disabled = false;
        btn.removeAttribute(BLOCK_ATTR);
      }
    } catch (e) {}
  }

  /* ---------------------------------------------------------------- run + install */

  function applyAll() {
    seatRakePanels();
    try { fixGaps(); } catch (e) {}
    var inst = instance();
    try { fixLatchLabels(inst); } catch (e) {}
    try { clearLegacyBlock(); } catch (e) {}
  }

  var timer = null;
  function schedule() {
    if (timer) clearTimeout(timer);
    timer = setTimeout(function () {
      applyAll();
      setTimeout(applyAll, 350);
      setTimeout(applyAll, 900);
    }, 120);
  }

  /* Seating and gaps are DOM-only, so they install anywhere .panel-preview is drawn - the
     calculator AND /pages/quote-review. This is deliberately separate from the prototype
     wrapping below, which can only work where the calculator class exists. */
  var domInstalled = false;
  function installDomOnly() {
    if (domInstalled) return;
    domInstalled = true;
    /* NO .panel-preview PRECONDITION. install() succeeds the moment the calculator class
       exists, which is BEFORE any section is drawn; tryInstall then stops polling. Gating
       this on a panel existing meant the observer was never attached on the calculator page
       and the seating never ran once - measured 2026-09-02, the rake artwork sat 72px above
       the ground line with no inline style on it at all. The seating itself no-ops when
       there are no panels, so attaching early costs nothing. */
    try {
      var root = document.querySelector('.calculator-content') ||
                 document.getElementById('qr-section-diagrams') ||
                 document.getElementById('section-preview') || document.body;
      if (window.MutationObserver) {
        new MutationObserver(schedule).observe(root, { childList: true, subtree: true });
      }
      window.addEventListener('resize', schedule);
    } catch (e) {}
    schedule();
    try { console.log('[cf-glass-preview-fixes] DOM seating installed'); } catch (e) {}
  }

  function install() {
    installDomOnly();

    var Ctor = window.GlassFencingCalculator ||
               (window.glassCalculator && window.glassCalculator.constructor);
    if (!Ctor || !Ctor.prototype) return false;
    var P = Ctor.prototype;
    if (P.__cfGlassPreviewFixes) return true;

    if (typeof P.showValidationPopup === 'function') {
      var orig = P.showValidationPopup;
      P.showValidationPopup = function (message, onOk) {
        try {
          var m = String(message == null ? '' : message);
          var s = m.replace(HINGE_MSG, '').replace(/\s{2,}/g, ' ')
                   .replace(/^[\s•\-–—.,;]+/, '').trim();
          if (!s) { if (typeof onOk === 'function') { try { onOk(); } catch (e) {} } return; }
          return orig.call(this, s, onOk);
        } catch (e) { return orig.apply(this, arguments); }
      };
    }

    /* THE GATE. validateSection is what handleSectionNextClick consults, and measured live it
       returns true for a raked section with no retained height. Returning false here is what
       stops the customer moving on, and the calculator's own popup does the explaining. */
    if (typeof P.validateSection === 'function') {
      var ov = P.validateSection;
      P.validateSection = function (n) {
        var ok;
        try { ok = ov.apply(this, arguments); } catch (e) { return ov.apply(this, arguments); }
        try {
          if (ok !== false && retainedMissing(this, n)) {
            flagField(n);
            var msg = RAKE_MSG + n + ' to continue.';
            if (typeof this.showValidationPopup === 'function') this.showValidationPopup(msg);
            else if (typeof this.showSectionValidationError === 'function') this.showSectionValidationError(n, msg);
            else try { window.alert(msg); } catch (e2) {}
            return false;
          }
          unflagField(n);
        } catch (e) {}
        return ok;
      };
    }

    if (typeof P.updateQuoteSummary === 'function') {
      var o = P.updateQuoteSummary;
      P.updateQuoteSummary = function () {
        var r = o.apply(this, arguments);
        try { fixLatchLabels(this); } catch (e) {}
        try { clearLegacyBlock(); } catch (e) {}
        return r;
      };
    }

    ['updateQuoteBreakdown', 'updateComponentsQuote'].forEach(function (mth) {
      if (typeof P[mth] !== 'function') return;
      var o2 = P[mth];
      P[mth] = function () {
        var r = o2.apply(this, arguments);
        try { fixLatchLabels(this); } catch (e) {}
        return r;
      };
    });

    ['renderSectionPreview', 'reRenderGaps'].forEach(function (mth) {
      if (typeof P[mth] !== 'function') return;
      var o3 = P[mth];
      P[mth] = function () {
        var r = o3.apply(this, arguments);
        try { fixGaps(); } catch (e) {}
        try { schedule(); } catch (e) {}
        return r;
      };
    });

    P.__cfGlassPreviewFixes = true;
    schedule();
    try { console.log('[cf-glass-preview-fixes] installed'); } catch (e) {}
    return true;
  }

  function tryInstall() {
    var done = false;
    try { done = install(); } catch (e) {}
    if (done) return;
    try { installDomOnly(); } catch (e) {}   /* quote-review has no calculator class */
    if (++TRIES < MAX_TRIES) setTimeout(tryInstall, 100);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', tryInstall);
  } else {
    tryInstall();
  }
})();
